// Single source of truth for date/time display — replaces the scattered
// toLocaleDateString()/toLocaleString() calls across the CRM that used
// inconsistent locales (en-US, en-IN, none) and no company-timezone concept,
// so "today"/day grouping could disagree between teammates in different zones.
const LOCALE = 'en-IN';
const DEFAULT_TIMEZONE = 'Asia/Kolkata';

// Reads the company's configured timezone out of the persisted Redux auth
// state (client/src/redux/store.js persists the whole `auth` slice to
// localStorage['authState'] on every action) without needing a live store
// subscription — safe to call from plain (non-hook) utility code as well as
// components. Prefer useCompanyTimezone() (hooks/useCompanySettings.js) inside
// React components — it re-renders immediately when Settings changes it.
export function getCompanyTimezone() {
  try {
    const raw = localStorage.getItem('authState');
    if (raw) {
      const auth = JSON.parse(raw);
      if (auth?.user?.timezone) return auth.user.timezone;
    }
  } catch (e) {
    // ignore malformed/missing storage — fall back to default below
  }
  return DEFAULT_TIMEZONE;
}

export function formatDate(value, { timeZone } = {}) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: timeZone || getCompanyTimezone(),
    year: 'numeric', month: 'short', day: '2-digit',
  }).format(d);
}

export function formatDateTime(value, { timeZone } = {}) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: timeZone || getCompanyTimezone(),
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
}

// 'YYYY-MM-DD' key for grouping events/tasks by calendar day in the company's
// timezone (instead of toDateString(), which uses the browser's local zone).
export function dayKey(value, { timeZone } = {}) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timeZone || getCompanyTimezone(),
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
}
